/**
 * 新規予約・変更申請で共有する「選択式」入力の候補値と、利用日範囲の
 * Asia/Tokyo基準バリデーション(iPhone実機確認で発見・是正)。
 *
 * 背景: iOS Safari(LINE LIFF内ブラウザ含む)の`<input type="time">`/
 * `<input type="number">`は、ネイティブUIが端末依存で自由入力になり、
 * 平日1時間単位("10:30"や"17:44"等)や不正な利用時間を作れてしまう。
 * 正本(実装指示書 v2.0)の業務ルール(平日は10:00〜23:00を1時間単位、
 * 土日祝は10:00〜23:00の1日貸し固定)はサーバー側Rule Engineが最終的に
 * 検証するが、UI側でも明示的な選択式にして「そもそも不正な値を作れない」
 * ようにする(料金計算ロジック自体はestimatePriceYen<pricing-estimate.ts>
 * を再利用し、ここでは候補値の生成とAsia/Tokyo日付範囲の判定のみを担う)。
 */

import { isWeekendDate } from "./pricing-estimate.js";

/** 平日の開始時刻候補(10:00〜22:00、1時間単位)。23:00開始は0時間になるため含めない。 */
export const WEEKDAY_START_TIME_OPTIONS: readonly string[] = Array.from(
  { length: 13 },
  (_, i) => `${String(10 + i).padStart(2, "0")}:00`,
);

/** 平日の利用時間候補(1〜13時間)。 */
export const WEEKDAY_DURATION_OPTIONS: readonly number[] = Array.from({ length: 13 }, (_, i) => i + 1);

/** 土日祝の固定枠(1日貸し、10:00〜23:00)。 */
export const WEEKEND_HOLIDAY_START_TIME = "10:00";
export const WEEKEND_HOLIDAY_END_TIME = "23:00";
export const WEEKEND_HOLIDAY_DURATION_HOURS = 13;

/** 土日祝の来館時刻候補(10:00〜22:00、1時間刻みのみ)。 */
export const ARRIVAL_TIME_OPTIONS: readonly string[] = WEEKDAY_START_TIME_OPTIONS;

export function computeEndTime(startTime: string, durationHours: number): string {
  const [h, m] = startTime.split(":").map(Number);
  const endHour = (h ?? 0) + durationHours;
  return `${String(endHour).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")}`;
}

/**
 * 指定した開始時刻から選べる利用時間候補(23:00を超えない範囲のみ)。
 * 例: 22:00開始なら1時間のみ、10:00開始なら1〜13時間すべて選べる。
 */
export function durationOptionsForStartTime(startTime: string): number[] {
  const [h] = startTime.split(":").map(Number);
  const startHour = h ?? 10;
  const maxDuration = Math.min(13, 23 - startHour);
  return WEEKDAY_DURATION_OPTIONS.filter((d) => d <= maxDuration);
}

const JST_DATE_FORMATTER = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** 現在時刻(UTC epoch ms)から、Asia/Tokyo基準の暦日("YYYY-MM-DD")を求める。 */
export function jstTodayIso(nowEpochMs: number = Date.now()): string {
  // en-CAロケールはYYYY-MM-DD形式を直接返すため、パーツの組み立てが不要。
  return JST_DATE_FORMATTER.format(new Date(nowEpochMs));
}

/**
 * "YYYY-MM-DD"へ暦月単位で加算する(負数で減算可)。対象月に存在しない日
 * (例: 1/31 + 1か月)は、その月の末日にクランプする(rental-booking/src/
 * core/business-date.tsのaddMonthsToBusinessDateと同じ丸め方針)。
 */
export function addMonthsToIsoDate(dateIso: string, months: number): string {
  const [yearStr, monthStr, dayStr] = dateIso.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  const zeroBasedMonthIndex = month - 1 + months;
  const targetYear = year + Math.floor(zeroBasedMonthIndex / 12);
  const targetMonth0 = ((zeroBasedMonthIndex % 12) + 12) % 12;
  const daysInTargetMonth = new Date(Date.UTC(targetYear, targetMonth0 + 1, 0)).getUTCDate();
  const clampedDay = Math.min(day, daysInTargetMonth);
  return `${String(targetYear).padStart(4, "0")}-${String(targetMonth0 + 1).padStart(2, "0")}-${String(clampedDay).padStart(2, "0")}`;
}

/**
 * 予約可能な利用日の範囲(Asia/Tokyo基準)。
 * 正本: 当日予約不可・利用日前日までに予約完了・最大3か月先まで。
 * min = 翌日、max = 今日から3か月後(暦月単位)。
 */
export function bookableDateRange(nowEpochMs: number = Date.now()): { min: string; max: string } {
  const today = jstTodayIso(nowEpochMs);
  const [y, m, d] = today.split("-").map(Number);
  const tomorrow = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + 1));
  const min = `${tomorrow.getUTCFullYear()}-${String(tomorrow.getUTCMonth() + 1).padStart(2, "0")}-${String(tomorrow.getUTCDate()).padStart(2, "0")}`;
  const max = addMonthsToIsoDate(today, 3);
  return { min, max };
}

/** 利用日(YYYY-MM-DD)が予約可能範囲(Asia/Tokyo基準)に入っているかを判定する。 */
export function isBookableDate(useDate: string, nowEpochMs: number = Date.now()): boolean {
  const { min, max } = bookableDateRange(nowEpochMs);
  return useDate >= min && useDate <= max;
}

/**
 * 土日祝1日貸し固定UI判定(Phase 11続行分「祝日をカレンダーUIでも正しく
 * 反映」)。土日はpricing-estimate.tsのisWeekendDate()(既存、frontendの
 * 唯一の曜日判定)、祝日はGET /v1/availability/datesのholiday_dates
 * (backend側Google Calendar「日本の祝日」判定結果)をそのまま使う。
 * frontend側で祝日を独自推測・計算しない。新規予約・変更申請の両画面が
 * この関数を共有することで、土日祝1日貸し固定UIへ切り替える条件を
 * 二重管理しない。
 */
export function isWeekendOrHolidayDate(useDate: string, holidayDates: ReadonlySet<string>): boolean {
  return isWeekendDate(useDate) || holidayDates.has(useDate);
}
